import { Component, type ErrorInfo, type ReactNode } from 'react';

import { isApiError } from '@/lib/api/errors';
import { captureError } from '@/lib/observability/sentry';
import { Button } from '@/ui/button';

interface ErrorFallbackProps {
    error: Error;
    reset: () => void;
}

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: (props: ErrorFallbackProps) => ReactNode;
}

interface ErrorBoundaryState {
    error: Error | null;
}

const INITIAL_STATE: ErrorBoundaryState = { error: null };

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    override state: ErrorBoundaryState = INITIAL_STATE;

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    override componentDidCatch(error: Error, info: ErrorInfo): void {
        captureError(error, { componentStack: info.componentStack });
    }

    reset = (): void => {
        this.setState(INITIAL_STATE);
    };

    override render(): ReactNode {
        const { error } = this.state;
        if (error === null) {
            return this.props.children;
        }
        const Fallback = this.props.fallback ?? DefaultErrorFallback;
        return <Fallback error={error} reset={this.reset} />;
    }
}

export function DefaultErrorFallback({ error, reset }: ErrorFallbackProps) {
    const requestId = isApiError(error) ? error.requestId : null;
    return (
        <div className="container flex min-h-[60vh] flex-col items-center justify-center gap-4 py-12 text-center">
            <div className="flex flex-col gap-2">
                <p className="font-display text-lg font-semibold text-foreground">
                    Something went wrong.
                </p>
                <p className="max-w-md text-sm text-muted-foreground">
                    The console hit an unexpected error. Your session is still active — retrying
                    usually fixes it. If the problem persists, share the request ID below with
                    Safariat support.
                </p>
            </div>
            <p className="rounded-md border border-border bg-muted px-3 py-1.5 font-mono text-2xs text-muted-foreground">
                {error.message}
            </p>
            {requestId ? (
                <p className="text-2xs text-muted-foreground">
                    Request ID:{' '}
                    <span className="font-mono text-foreground">{requestId}</span>
                </p>
            ) : null}
            <Button variant="primary" onClick={reset}>
                Try again
            </Button>
        </div>
    );
}

export type { ErrorFallbackProps };
